import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Fixing laggy UI with React.memo",
  "description": "",
  "published": "2019-03-18T00:00:00.000Z",
  "redirect_from": ["/blog/fixing-laggy-ui-with-react-memo/swizec/8988"],
  "categories": "Front End, Technical",
  "hero": "./img/media-D11LdPWUcAA6SVw.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The other day I was dealing with some laggy UI on a project and discovered React.memo as a way of fixing it. I don't know how long React.memo has been a thing but it's new to me and it's amazing.`}</p>
    <p>{`And when I say laggy UI, I mean `}<em parentName="p">{`really`}</em>{` laggy UI.`}</p>
    <p>{`Here's a gif of me typing full speed at 120wpm into a form.`}</p>
    <p><img parentName="p" {...{
        "src": "/73bb025d0a29ef77d6fd990ab47373a0/VlpnEbl.gif",
        "alt": null
      }}></img></p>
    <p>{`Yeah, real bad. 🙈`}</p>
    <p>{`After a few small tweaks tho ...`}</p>
    <p><img parentName="p" {...{
        "src": "/6186918c074a5c124478630d451c002b/ihNGHUN.gif",
        "alt": null
      }}></img></p>
    <p>{`Let's start with how #1 happened, then what you might do to get to #2. .`}</p>
    <p>{`PS: this article is heavy in gifs, please be patient`}</p>
    <h2 {...{
      "id": "how-to-make-a-slow-ui"
    }}>{`How to make a slow UI`}</h2>
    <p>{`There's a few key components to a slow UI and they all have to do with doing too much on too many state updates. I can't share the exact codebase so here's a rough analog I built in CodeSandbox.`}</p>
    <p><a parentName="p" {...{
        "href": "https://codesandbox.io/s/n7zlz8onj0"
      }}><img parentName="a" {...{
          "src": "/c693a9cba0d4f21a10a1607f7b355903/NuogZIB.gif",
          "alt": "Click me to play with code"
        }}></img></a></p>
    <p>{`In the original code my state structure follows the unidirectional dataflow principle. State for all components lives in `}<inlineCode parentName="p">{`App`}</inlineCode>{` and flows down via props.`}</p>
    <p>{`The state object is deep and convoluted so for ease of use, `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` callbacks are colocated with state.`}</p>
    <p>{`Many components rely on this state which means that each keystroke re-renders the whole UI.`}</p>
    <ul>
      <li parentName="ul">{`a bunch of tags`}</li>
      <li parentName="ul">{`a big dataviz`}</li>
      <li parentName="ul">{`many tooltips`}</li>
      <li parentName="ul">{`a few things I can't remember`}</li>
    </ul>
    <p>{`The underlying reason why each keystroke leads to a full re-render is that I'm treating state as immutable. Every keystroke creates a new copy of the entire state tree.`}</p>
    <p>{`JavaScript compares objects by reference.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">{foo: 'bar'} === {foo: 'bar'} // false
`}</code></pre>
    <p>{`Great for making sure your whole UI is always up to date. Bad for performance.`}</p>
    <h2 {...{
      "id": "react-time-slicing"
    }}>{`React time slicing`}</h2>
    <p>{`First avenue of attack is to debounce user input. Update your UI asynchronously on each keystroke instead of forcing your user to wait.`}</p>
    <p>{`Two ways you can approach this:`}</p>
    <ol>
      <li parentName="ol">{`Turn your `}<inlineCode parentName="li">{`<input>`}</inlineCode>{` into a component, use local state to drive its value, call the global `}<inlineCode parentName="li">{`onChange`}</inlineCode>{` callback every few milliseconds. This works but increases complexity.`}</li>
    </ol>
    <p>{`2) Use React time slicing and make `}<inlineCode parentName="p">{`this.setState`}</inlineCode>{` itself asynchronous. Can't remember which version of React this came with, maybe 16.4 ... it works real good.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">onChange: value => {
`}</code></pre>
    <p>{`   let { values } = this.state;
values = `}{`[...values]`}{`;
values.push(value);
values`}{`[0]`}{` = value;`}</p>
    <p>{`   this.setState({ values });
}`}</p>
    <p>{`👇`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">onChange: value => {
  let { values } = this.state;
  values = [...values];
  values.push(value);
  values[0] = value;

  window.requestIdleCallback(() => this.setState({ values }));
}
`}</code></pre>
    <p>{`Instead of immediately updating our state, we wait for a browser idle callback. Basically saying `}<em parentName="p">{`"Yo browser, do this when you got some time will ya?"`}</em></p>
    <p>{`The result is a faster app with a somewhat cumbersome typing experience.`}</p>
    <p><a parentName="p" {...{
        "href": "https://codesandbox.io/s/n7zlz8onj0"
      }}><img parentName="a" {...{
          "src": "/0d4b103a95c7b34357d13b4f0bd44c64/gNHQMmd.gif",
          "alt": "Click to explore code"
        }}></img></a></p>
    <h2 {...{
      "id": "reactmemo"
    }}>{`React.memo`}</h2>
    <p>{`Your best bet isn't to make updates asynchronous, it's to avoid them completely.`}</p>
    <p>{`In ye olden days you'd do that with `}<inlineCode parentName="p">{`shouldComponentUpdate`}</inlineCode>{`. It's 2019 now and we don't do that anymore. We've got something much better 👉 `}<inlineCode parentName="p">{`React.memo`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`React.memo`}</inlineCode>{` takes a functional React component and memoizes it. First param says what to memoize, second param says when to bust the cache.`}</p>
    <p>{`It's a lot like `}<inlineCode parentName="p">{`useEffect`}</inlineCode>{` but for entire components. You tell React exactly when you'd like to re-render.`}</p>
    <p>{`Like this`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">export const MemoRecursive = React.memo(
  ({ values }) => {
    // render values
  },
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) === JSON.stringify(nextProps)
);
`}</code></pre>
    <p>{`First argument is a functional component. When rendering, React.memo calls your comparison function, the second argument, and if that returns `}<inlineCode parentName="p">{`true`}</inlineCode>{`, it updates your component.`}</p>
    <p>{`I'm using JSON.stringify as a dirty deep equals. You might want to use one of the many fast deep equals libraries out there. Or maybe there's a specific prop value you can check.`}</p>
    <p>{`The result is an app with smooth UI`}</p>
    <p><a parentName="p" {...{
        "href": "https://codesandbox.io/s/n7zlz8onj0"
      }}><img parentName="a" {...{
          "src": "/0f622fee7712a103890fff19e5a63a20/Z5hBlzu.gif",
          "alt": "Click to experiment yourself"
        }}></img></a></p>
    <p>{`Using recursion was a bad example it turns out. Props are never the same so memoization can't kick in. Oops.`}</p>
    <p>{`Tried fixing it by building the array head-first instead of tail-first. Didn't quite work.`}</p>
    <p>{`In the real-world, though, React.memo made a huge difference! Compare these outputs from the Chrome Profiler. I was typing `}<inlineCode parentName="p">{`this is a test`}</inlineCode>{` into my input box both times.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/f573ee7e7a45d3a4ce861e3eda6f246f/c1b63/media-D11LdPWUcAA6SVw.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "14.798206278026903%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'59\\'%20viewBox=\\'0%200%20400%2059\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M60%203c0%202%201%202%206%202h7L61%206%2048%207h126l-12-1c-10%200-11-1-11-2%200-2-1-2-4-2-7%200-7%203-1%203a585%20585%200%2001-86-2M20%2017l-1%204v2h188a12581%2012581%200%2000183-1c-5%200-5%200-6-2l-2-4-182-1C29%2015%2020%2015%2020%2017M0%2049l200%201A19400%2019400%200%20000%2049\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/f573ee7e7a45d3a4ce861e3eda6f246f/ca0a1/media-D11LdPWUcAA6SVw.webp 223w", "/static/f573ee7e7a45d3a4ce861e3eda6f246f/75680/media-D11LdPWUcAA6SVw.webp 445w", "/static/f573ee7e7a45d3a4ce861e3eda6f246f/8d1ba/media-D11LdPWUcAA6SVw.webp 890w", "/static/f573ee7e7a45d3a4ce861e3eda6f246f/92f8c/media-D11LdPWUcAA6SVw.webp 1200w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/f573ee7e7a45d3a4ce861e3eda6f246f/e92b6/media-D11LdPWUcAA6SVw.png 223w", "/static/f573ee7e7a45d3a4ce861e3eda6f246f/e66bf/media-D11LdPWUcAA6SVw.png 445w", "/static/f573ee7e7a45d3a4ce861e3eda6f246f/4ef49/media-D11LdPWUcAA6SVw.png 890w", "/static/f573ee7e7a45d3a4ce861e3eda6f246f/c1b63/media-D11LdPWUcAA6SVw.png 1200w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/f573ee7e7a45d3a4ce861e3eda6f246f/4ef49/media-D11LdPWUcAA6SVw.png",
              "alt": "media D11LdPWUcAA6SVw",
              "title": "media D11LdPWUcAA6SVw",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/d9f691575aad991925b09b749ab29530/82d6d/media-D11Lj3MVYAAdZ6E.jpg",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "30.493273542600896%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'121\\'%20viewBox=\\'0%200%20400%20121\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M283%207h-1c-1-2-4-1-4%200s1%202%202%201c1%200%201%201-1%202-3%202-1%204%205%203%204-1%206%200%203%202h-1l-1-1v1l-1%201-1-1h-4l-1%202-1%202-1%207c0%204%200%206%201%204%201-4%2012-5%2012-1l1%202%201-3c0-2-1-2-6-2h-6v-9l5%201c7%201%207%201%206-3%200-3%200-3%202-2%202%200%203%200%203-2%202-3%200-5-4-5h-6c0-1-1-1-2%201m-67%201v2c-2%202-1%203%201%203l2-2c0-2%200-2%201%200%201%201%202%202%207%202s6-1%206-3V7l-9-1c-6%200-8%201-8%202m125-1l1%202v1c-2%200-1%203%201%203l2-1c-1-2%200-2%201-1l7%202c5%200%206-1%206-3%200-3-1-4-5-4l-7%202h-2c-1-2-4-2-4-1M58%2017l-5%201c-5%200-5%201-5%203%200%203%200%203-4%203-5%200-5%200-5-2l-1-3-1%206%201%206%201-3c0-3%200-3%204-3l5%201c1%201%201-2%201-5l5-1c4%200%204%200%204%203v3l6-1%206-1%203-1c2%200%202%200%202%202s0%202%205%202c7%200%2010%201%2010%203l1%201%201-2c0-3%200-3-8-3-7%200-8-1-8-3s-1-2-3-2l-4-1c-1-1-1-1-1%201%200%203%200%203-4%203h-5v-4l-1-3M22%2035l-1%208-1%207h13l12-1c0-1-4-4-5-3l-2-5-2-6c0-2-1-2-7-2-5%200-6%200-7%202m33-1l-3%2012-1-3c-1-5-3-5-3%200l-1%205c-1%202%200%202%2022%202s23%200%2023-2v-5l-1-1v-2l-1-2c0-3-4-5-10-5l-4%201-2%206c-1%208-1%208-4%204l-1-3-1-1-1-3c-1-4-1-4-7-4l-5%201m53%202l-1%204-1%204-2%204-1%202h32a229%20229%200%200030-2v-1l-5-10c-1-3-1-3-6-3h-5l-2%205c0%204-4%2010-5%208l-4-7-3-7h-26l-1%203m73-1c0%203-6%2011-9%2014-1%201%202%201%2017%201l19-1h-1c-2%200-3-1-4-9%200-6-1-7-13-7-9%200-9%200-9%202m56-1c-1%200-2%201-2%204l-2%208-1%204h19c13%200%2019%200%2018-1h-3c-1%200-2-1-2-4%200-5-2-11-4-12s-6%201-6%203l-1%203-2%203c-1%203-3%202-3-2-2-7-4-8-11-6m42%200l-2%207-2%207c-3%202-2%202%2010%202l10-1c-4-1-4-1-7-14%200-2-7-3-9-1m27%201l-2%204-3%206-2%205h39l-1-2-2-5-2-6c-1-3-1-3-5-3-3%200-4%200-4%203l-2%203-2%204c0%205-3%205-3%200%200-4-4-11-7-11l-4%202m47%201l-2%205c-1%204-5%208-8%207l-2%201c-1%201%204%201%2016%201s17%200%2015-1c-5-1-8-5-9-11%200-4-1-5-4-5s-4%200-6%203m22%2062h24v5l-45-1-45%201%2091%201v-7h-48l23%201m-322%209c-2%202-1%207%202%207l2-1h2c2%202%202%202%203%201l2-1c2%202%205%201%205-1v-4c1-1-3-2-4-1-1%202-1%202-3%200h-9m280%200v1l1%203c-1%202-1%203%201%203l2-1h1c0%202%204%201%205-1h1c1%202%204%203%204%201h6l1-2c0-3-3-6-4-4h-8c-2-1-4%200-6%203l-1%203v-3c0-3-1-4-3-3m-76%2012l-128%201a8506%208506%200%200071%201%2023786%2023786%200%200058-2h-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/d9f691575aad991925b09b749ab29530/ca0a1/media-D11Lj3MVYAAdZ6E.webp 223w", "/static/d9f691575aad991925b09b749ab29530/75680/media-D11Lj3MVYAAdZ6E.webp 445w", "/static/d9f691575aad991925b09b749ab29530/8d1ba/media-D11Lj3MVYAAdZ6E.webp 890w", "/static/d9f691575aad991925b09b749ab29530/c76c7/media-D11Lj3MVYAAdZ6E.webp 962w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/d9f691575aad991925b09b749ab29530/853b2/media-D11Lj3MVYAAdZ6E.jpg 223w", "/static/d9f691575aad991925b09b749ab29530/0a4fe/media-D11Lj3MVYAAdZ6E.jpg 445w", "/static/d9f691575aad991925b09b749ab29530/7f80b/media-D11Lj3MVYAAdZ6E.jpg 890w", "/static/d9f691575aad991925b09b749ab29530/82d6d/media-D11Lj3MVYAAdZ6E.jpg 962w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/jpeg"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/d9f691575aad991925b09b749ab29530/7f80b/media-D11Lj3MVYAAdZ6E.jpg",
              "alt": "media D11Lj3MVYAAdZ6E",
              "title": "media D11Lj3MVYAAdZ6E",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`Took 4 seconds to type and render without these optimizations. 1 second with. 🤘`}</p>
    <h1 {...{
      "id": "in-other-news"
    }}>{`In other news`}</h1>
    <p>{`In other news, my two Reactathon workshops are almost fully sold out. You should consider joining, if you're in town. Great conference too. I'll be there hanging out all weekend :)`}</p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://ti.to/real-world-react/react-and-d3-workshop"
      }}>{`React for DataViz workshop`}</a>{` 👉 `}<a parentName="p" {...{
        "href": "https://ti.to/real-world-react/modern-app-with-react-workshop"
      }}>{`Build a modern app workshop`}</a></p>
    <p>{`Next two weeks are going to be intense with prep for those workshops. But it's okay, I'm running a marathon on Sunday to give myself a break.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1107366186272980992"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "480px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "116.59192825112108%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'466\\'%20viewBox=\\'0%200%20400%20466\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2021c-5%203-7%208-7%2014l2%206%201%201c0%203%206%206%2011%207%207%200%2015-5%209-6-5-1-3-6%202-6l2-2h1l2-1-4-10-3-2c-3-3-11-4-16-1m178%2041c-4%204-1%2011%204%2011%203%200%206-4%206-7-1-6-6-8-10-4M27%20108c-2%202-2%202%201%206l5%205%201%203v-3c0-2%203-4%207-4%206%200%2024%200%2022-1-4%200-8-2-8-3l10%202%206%202%202%202c0%201%201%201%202-1h3c0-2%205-1%206%202l2%202%201-2c1-3%202-3%205-3%204%200%204%201%206%205%202%203%203%204%203%202l1-2%201-2h3c0%201%201%202%204%201h9l5-1a102%20102%200%200142%202l19%201c12-1%2014-1%2018%201l8%202%204%201-1-3c-2-6%203-8%209-3%203%203%207%203%208%200%201-2%204-3%204-1h2c2-2%205-1%208%202l4%203v-2c0-2%201-3%202-2%202%200%202%200%201-1-2-1%200-2%203-2%204%200%205%202%204%204v2l1%202%202%201v-2l-1-3%202%202c0%202%202%203%203%201l4-2%204-1h3l8%202c3%203%2013%206%2013%204l3-1%204-1%203-2%202-2h4c4%202%205%202%207%200%201-1%203-2%204-1l4-1h2l3%202%205%203%205%202c7%200%2017-2%2021-4%205-3%206-2%203%202-2%202-3%204-2%205%200%203%202%204%203%203l3-1%203%201v-11c0-9%200-10-2-12-3-3-343-3-346%200m17%2012l1%202v6c-2%201-2%202%202%204l6%203h1l3%201c4%200%205-2%203-4-1%200-1-1%201-1%202-1%202-1-1-2-3%200-3%200-3-5l-1-4c-2%200-2%201-2%205s0%204-2%204c-3%200-3%200-3-4l-1-5-2-1-2%201m199%2013c-21%204-37%2014-49%2027-7%209-8%2015-2%2023%205%209%203%2019-6%2020l-22%207a88%2088%200%2001-14%204l-3%201-11%209h-12l-1-15a498%20498%200%2000-5-49c-1%202-8%203-10%201l-3-2%202%2069c1%202%205%203%205%201l3-1c3%200%203%200%202%202l1%202h3c0-2%204%202%204%204s-2%203-2%201c0%200-2%200-3%202-2%201-3%202-2%203l1%202%202%204%202%204%204%206%203%206%201%201%205%209%207%2010%202%203%202%204c1%202%201%204-1%204l-1-1-1-2-2-3-4-4c-3-1-3-1-2-2h2l-4-7-2-4c-1-4-3-5-4-3-1%201-1%201-1-1%200-3-1-7-3-9l-3-4c0-2-2-3-2-3l-3-2-2-2-1-2-4-4-3-4v-1l-1-3c0-3%200-4-2-3l-2-1-2-4-3-3-15%201a467%20467%200%2001-39%200H25v8l1%208h61v5l1%2017-4-1c-4-3-7-2-8%201l1%204%201%203%202%202c2%200%202%201%201%202l-5%203-3%202c-2%202-3%202-4%201-2-3-13%204-13%209l1%203h2l-2%204c-2%203-4%204-4%203l2-4%201-2-16%209c-1%202-9%205-11%205l-1-1-2-5-1-2%201%204v6l2%202%202%202-1%202-1%201v2c2%201%200%203-2%203-1-1-1-1-2%2029%200%2015%200%2016%202%2019l2%202h344l2-3c2-2%202-3%202-40l-1-38-2%204c-1%202-2%203-3%202v-1l-1-1c-2%200-10-8-10-10s1-2%208-1h9v-18h-8a192%20192%200%2000-18-2h21c5%201%206-2%204-9%200-4%200-4-5-4l-4%201%204%201%204%201h-2a157%20157%200%2000-10-1c6-1%201-2-7-2s-9%200-9%202c1%201%200%201-5%201-7%200-5-2%202-2%202%200%202%200%200-1l-2-1%202-1c4-1%201-2-8-2-5%200-8%200-7%201%204%201%203%206-2%205a369%20369%200%2000-30-2v-1c-4%200%201-2%206-2l6-1h-7l-6-1%202-1v-1c-2-1-1-4%201-3%202%200%202%200%202-2l2-3c1-1%201-1%201%201-1%203%200%203%203%200%202-2%203-3%203-1s0%203%202%203%202%200%201-1c-2-1-2-1%200-1l2-2%202-1c1-1%201-1%201%201-1%201-1%201%201%201h1v2l2%201c1%201%205%200%205-1l-1-1-2-1-2-1c-4%200-2-4%203-5%202%200%204-1%204-3h3l1-1c2%200%201%202-1%203l-2%202%203-1%203%201c1%201%201%202-1%202-3%200-4%200-3%201l-1%201c-2%200-3%202-3%203h6l1-1v-1c2%200%202%201%201%203-1%201-3%202-4%201l-2%201c1%201%200%202-1%202-4%201-2%202%201%203l6-2c3-3%204-4%203-1l-1%202c0%202%207%201%208%200h1l4%201%205%201h-5c-3%200-4%200-4%202l15%201h14v-75h-10l-10%201h-1l-10-1c-10%201-12%201-8%203h-1c-7-1-34-1-36%201-3%201-7%200-7-2%200-1%202-4%205-6%209-10%2010-15%204-21-12-12-39-18-58-15m-135%2049v5c0%203%201%204%206%204%204%200%205-1%205-3%200-5-2-7-6-7l-5%201m100%2011v4h-2c-2-1-3%200-1%204l2%205c0%203%200%203%203%203%204-2%206-4%204-7l-1-5-2-5c-2-2-2-2-3%201m-98%204c-3%203%200%2012%203%2012%205%200%206-1%206-6-1-6-5-9-9-6m212%2015l-14%203c-3%200-5%202-2%203l3%201h-3l-2%202%201%201h3l1-2h1l1-1c-1-2-1-2%201-2h3c0-1%201-1%202%201%201%201%201%201%202-1h7l2-1c3%200%207-3%206-3h-10l-2-1m-102%2038l1%201c1%200%204%208%204%2011l1%203%201%205c0%203%200%203-1%202l-4-2c-2%200-7-5-7-7l-2-3v4c1%2017%202%2027%200%2029-2%203-1%209%202%2012l4%208%203%208c2%203%201-8%200-12-2-4-2-5%200-3%201%202%201%202%201%200l1-5c0-2%200-3-1-2-1%202-2%200-2-9v-10c2%200%2010%204%2013%208l4%202c2-2%201-4-1-4l-3-3-1-3-1-3-2-2v-1c2-1-1-18-5-24h-5M80%20406c-3%202-5%206-3%2010s9%205%2012%201c2-3%200-11-3-11h-6m54%200c-2%201-4%206-3%209%201%205%208%207%2012%203%206-6-1-16-9-12m18%200c-3%203-4%206-3%209%202%206%208%207%2013%202%206-6-3-16-10-11m18%201c-2%201-3%203%200%203%201%201%201%201-1%202-3%201-2%204%202%205%208%201%2010%200%2010-4l-1-2h-3l2-1c2%200%202%200%200-3-4-2-6-3-9%200m19-1c-5%202-5%209-1%2012%207%206%2016-4%2010-11-3-2-5-2-9-1m16%201c-3%203-3%207%200%2010%204%206%2013%203%2013-4%200-4-4-8-7-8l-6%202m21%200l-1%203c1%202%201%203-1%202-2%200-2%200-2%202%200%205%208%208%2012%204%202-2%203-8%201-8-2-1-2%200-2%202%200%203-2%200-3-2-2-4-2-4-4-3\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/34c5a7514642468561507e192b775995/ca0a1/techletter.app-screenshot-1552930361466.webp 223w", "/static/34c5a7514642468561507e192b775995/75680/techletter.app-screenshot-1552930361466.webp 445w", "/static/34c5a7514642468561507e192b775995/d3be9/techletter.app-screenshot-1552930361466.webp 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/34c5a7514642468561507e192b775995/e92b6/techletter.app-screenshot-1552930361466.png 223w", "/static/34c5a7514642468561507e192b775995/e66bf/techletter.app-screenshot-1552930361466.png 445w", "/static/34c5a7514642468561507e192b775995/e85cb/techletter.app-screenshot-1552930361466.png 480w"],
                "sizes": "(max-width: 480px) 100vw, 480px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/34c5a7514642468561507e192b775995/e85cb/techletter.app-screenshot-1552930361466.png",
                "alt": "https://twitter.com/Swizec/status/1107366186272980992",
                "title": "https://twitter.com/Swizec/status/1107366186272980992",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`https://twitter.com/Swizec/status/1107366186272980992`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Enjoy your week ❤️`}</p>
    <p>{`Cheers, ~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      